import { FINNHUB_VALUATION_ENABLED } from "../constants/features";
import { callFinnhub } from "./finnhubClient";
// Earnings calendar — pulls the next 7 days of reports from Finnhub
// (/calendar/earnings) and ranks them by usefulness:
//   1) symbols the user already holds or watches
//   2) then by analyst-revenue-estimate size (proxy for "well-followed")
//
// The shape returned per row is exactly the fields the card needs:
//   { symbol, name, date, hour, epsEstimate, revenueEstimate }
//
// All values are CONSENSUS analyst estimates from Finnhub — we surface them
// as facts ("the bar"), never as our forecast. The card layer adds the
// "מידע בלבד · אינו ייעוץ" disclaimer.
//
// Cache: in-memory, keyed by user-symbols set, 1-hour TTL. Any error → [].

const TTL  = 60 * 60 * 1000; // 1h

const _cache = new Map(); // cacheKey → { ts, data }

// Small symbol→company name map for the most-traded names that show up
// frequently in earnings weeks. Anything not in the map renders with the
// symbol only — never invent a name.
const SYMBOL_NAMES = {
  AAPL:  "Apple",
  MSFT:  "Microsoft",
  GOOGL: "Alphabet (Class A)",
  GOOG:  "Alphabet (Class C)",
  AMZN:  "Amazon",
  META:  "Meta Platforms",
  NVDA:  "NVIDIA",
  TSLA:  "Tesla",
  ORCL:  "Oracle",
  ADBE:  "Adobe",
  CRM:   "Salesforce",
  AVGO:  "Broadcom",
  INTC:  "Intel",
  AMD:   "AMD",
  QCOM:  "Qualcomm",
  TSM:   "TSMC",
  NFLX:  "Netflix",
  JPM:   "JPMorgan Chase",
  GS:    "Goldman Sachs",
  BAC:   "Bank of America",
  C:     "Citigroup",
  WFC:   "Wells Fargo",
  MS:    "Morgan Stanley",
  V:     "Visa",
  MA:    "Mastercard",
  XOM:   "Exxon Mobil",
  CVX:   "Chevron",
  WMT:   "Walmart",
  COST:  "Costco",
  HD:    "Home Depot",
  LOW:   "Lowe's",
  NKE:   "Nike",
  DIS:   "Disney",
  KO:    "Coca-Cola",
  PEP:   "PepsiCo",
  MCD:   "McDonald's",
  PFE:   "Pfizer",
  JNJ:   "Johnson & Johnson",
  UNH:   "UnitedHealth",
  LEN:   "Lennar",
  CHWY:  "Chewy",
  CASY:  "Casey's General Stores",
  SJM:   "J.M. Smucker",
  CPB:   "Campbell Soup",
  ABM:   "ABM Industries",
  UNFI:  "United Natural Foods",
  THO:   "Thor Industries",
};

function isoDate(d) { return d.toISOString().slice(0, 10); }

// Returns up to ~10 prominent earnings reports for the next 7 days.
// userSymbols: array of stock symbols the user holds or watches (string[]).
// `days`: how far ahead · 7 for the week card, 14 for the home screen's
// "אירועים קרובים" (sprint 18, stage 3).
export async function fetchEarningsCalendar(userSymbols = [], { days = 7 } = {}) {
  const userSet = new Set(userSymbols);
  const cacheKey = `${[...userSet].sort().join(",")}|${isoDate(new Date())}|${days}`;
  const hit = _cache.get(cacheKey);
  if (hit && Date.now() - hit.ts < TTL) return hit.data;

  const today = new Date();
  const week  = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);

  // Through the proxy — the key is server-side only now.
  const d = await callFinnhub("earnings", { from: isoDate(today), to: isoDate(week) });
  const rows = Array.isArray(d?.calendar?.earningsCalendar) ? d.calendar.earningsCalendar : [];
  if (!rows.length) return [];

  // Drop rows missing both estimates — without numbers the card can't show
  // an analyst consensus, so there's nothing to render factually. While the
  // estimates are hidden (FINNHUB_VALUATION_ENABLED, constants/features.js)
  // the card shows report dates only, so a row needs a symbol and nothing else.
  const withEstimates = rows.filter(
    r => r.symbol && (!FINNHUB_VALUATION_ENABLED || (r.epsEstimate != null && r.revenueEstimate != null))
  );

  // Rank: portfolio/watchlist symbols first, then by revenue-estimate size.
  withEstimates.sort((a, b) => {
    const aMine = userSet.has(a.symbol) ? 1 : 0;
    const bMine = userSet.has(b.symbol) ? 1 : 0;
    if (aMine !== bMine) return bMine - aMine;
    return (b.revenueEstimate || 0) - (a.revenueEstimate || 0);
  });

  // De-duplicate by symbol (Finnhub occasionally lists the same ticker twice
  // when an earnings date moves) and cap at 10.
  const seen = new Set();
  const picked = [];
  for (const r of withEstimates) {
    if (seen.has(r.symbol)) continue;
    seen.add(r.symbol);
    picked.push({
      symbol:          r.symbol,
      name:            SYMBOL_NAMES[r.symbol] || null,
      date:            r.date,
      hour:            r.hour || "",
      epsEstimate:     r.epsEstimate,
      revenueEstimate: r.revenueEstimate,
      isUserHolding:   userSet.has(r.symbol),
    });
    if (picked.length >= 10) break;
  }

  _cache.set(cacheKey, { ts: Date.now(), data: picked });
  return picked;
}
